import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, takeUntil, debounceTime, distinctUntilChanged, switchMap, of } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { SearchService, SearchResponse } from '../services/search.service';
import { SearchComponent } from './search.component';
import { PendingInventoryComponent } from './pending-inventory.component';
import { ApiService } from '../services/api.service';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

interface ResultGroup {
  label: string;
  icon: string;
  route: string;
  items: { id: number; title: string; subtitle?: string; status?: string }[];
}

interface InventoryTransaction {
  id: number;
  created: Date | null;
  createdBy: string;
  inventoryTransactionType: string;
  quantity: number;

  productId: number | null;
  catalogNum: string;
  lotNumNumber: string;
  salesOrderId: number | null;
  salesOrderNumber: string;
  purchaseOrderId: number | null;
  purchaseOrderNumber: string;
  invoiceId: number | null;
  invoiceNumber: string;
}

@Component({
  standalone: true,
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.css',
  imports: [
    CommonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatChipsModule,
    MatDividerModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    MatFormFieldModule,
    PendingInventoryComponent
]
})
export class HomeComponent implements OnInit, OnDestroy {
  // Inline search results
  searchQuery: string = '';
  searchLoading: boolean = false;
  searchError: string | null = null;
  resultGroups: ResultGroup[] = [];
  totalCount: number = 0;
  dataSource = new MatTableDataSource<InventoryTransaction>([]);
  displayedColumns: string[] = ['created', 'createdBy', 'inventoryTransactionType', 'quantity', 'catalogNum', 'lotNum', 'invoice', 'salesOrder', 'purchaseOrder'];
  loading: boolean = true;
  error: string | null = null;
  private destroy$ = new Subject<void>();

  @ViewChild(MatSort) set matSort(s: MatSort) {
    if (s) { this.dataSource.sort = s; }
  }

  @ViewChild(MatPaginator) set matPaginator(p: MatPaginator) {
    if (p) { this.dataSource.paginator = p; }
  }

  constructor(
    private router: Router,
    private searchService: SearchService,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {

    this.loadInventoryTransactions();
    // this.searchService.query$
    //   .pipe(
    //     takeUntil(this.destroy$),
    //     debounceTime(350),
    //     distinctUntilChanged(),
    //     switchMap(query => {
    //       this.searchQuery = query;
    //       if (!query.trim()) {
    //         this.resultGroups = [];
    //         this.totalCount = 0;
    //         this.searchLoading = false;
    //         this.searchError = null;
    //         this.cdr.detectChanges();
    //         return of(null);
    //       }
    //       this.searchLoading = true;
    //       this.searchError = null;
    //       this.cdr.detectChanges();
    //       return this.searchService.search(query.trim());
    //     })
    //   )
    //   .subscribe({
    //     next: (data) => {
    //       if (data === null) return;
    //       this.buildGroups(data as SearchResponse);
    //       this.searchLoading = false;
    //       this.cdr.detectChanges();
    //     },
    //     error: (err) => {
    //       console.error('Search error:', err);
    //       this.searchError = 'Search failed. Please try again.';
    //       this.searchLoading = false;
    //       this.cdr.detectChanges();
    //     }
    //   });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildGroups(data: SearchResponse): void {
    const groups: ResultGroup[] = [];

    if (data.products?.length) {
      groups.push({
        label: 'Products', icon: 'inventory_2', route: '/products',
        items: data.products.map(p => ({ id: p.id || p.Id, title: p.name || p.Name || `Product ${p.id || p.Id}`, subtitle: p.catalogNum || p.CatalogNum || '' }))
      });
    }
    if (data.customers?.length) {
      groups.push({
        label: 'Customers', icon: 'people', route: '/customers',
        items: data.customers.map(c => ({ id: c.id || c.Id, title: c.name || c.Name || `Customer ${c.id || c.Id}`, subtitle: c.email || c.Email || '' }))
      });
    }
    if (data.vendors?.length) {
      groups.push({
        label: 'Vendors', icon: 'storefront', route: '/vendors',
        items: data.vendors.map(v => ({ id: v.id || v.Id, title: v.name || v.Name || `Vendor ${v.id || v.Id}`, subtitle: v.email || v.Email || '' }))
      });
    }
    if (data.salesOrders?.length) {
      groups.push({
        label: 'Sales Orders', icon: 'receipt_long', route: '/orders',
        items: data.salesOrders.map(so => ({ id: so.id || so.Id, title: so.number || so.Number || `Order ${so.id || so.Id}`, subtitle: so.customerName || so.CustomerName || '', status: so.status || so.Status || '' }))
      });
    }
    if (data.purchaseOrders?.length) {
      groups.push({
        label: 'Purchase Orders', icon: 'shopping_cart', route: '/purchase-orders',
        items: data.purchaseOrders.map(po => ({ id: po.id || po.Id, title: po.number || po.Number || `PO ${po.id || po.Id}`, subtitle: po.vendorName || po.VendorName || '', status: po.status || po.Status || '' }))
      });
    }
    if (data.salesInvoices?.length) {
      groups.push({
        label: 'Sales Invoices', icon: 'description', route: '/sales-invoices',
        items: data.salesInvoices.map(inv => ({ id: inv.id || inv.Id, title: inv.invoiceNumber || inv.InvoiceNumber || `Invoice ${inv.id || inv.Id}`, subtitle: inv.customerName || inv.CustomerName || '', status: inv.status || inv.Status || '' }))
      });
    }
    if (data.proformas?.length) {
      groups.push({
        label: 'Proformas', icon: 'article', route: '/proformas',
        items: data.proformas.map(pf => ({ id: pf.id || pf.Id, title: pf.proformaNumber || pf.ProformaNumber || `Proforma ${pf.id || pf.Id}`, subtitle: pf.customerName || pf.CustomerName || '', status: pf.status || pf.Status || '' }))
      });
    }

    this.resultGroups = groups;
    this.totalCount = groups.reduce((sum, g) => sum + g.items.length, 0);
  }

  navigateTo(route: string, id: number): void {
    this.router.navigate([route, id]);
  }

  viewAll(route: string): void {
    this.router.navigate([route]);
  }

  goToProduct(productId: number | null | undefined, event?: Event): void {
    event?.stopPropagation();
    if (productId) {
      this.router.navigate(['/products', productId]);
    }
  }

  goToSalesOrder(salesOrderId: number | null | undefined, event?: Event): void {
    event?.stopPropagation();
    if (salesOrderId) {
      this.router.navigate(['/orders', salesOrderId]);
    }
  }

  goToPurchaseOrder(purchaseOrderId: number | null | undefined, event?: Event): void {
    event?.stopPropagation();
    if (purchaseOrderId) {
      this.router.navigate(['/purchase-orders', purchaseOrderId]);
    }
  }

  goToInvoice(invoiceId: number | null | undefined, event?: Event): void {
    event?.stopPropagation();
    if (invoiceId) {
      this.router.navigate(['/sales-invoices', invoiceId]);
    }
  }

  /**
   * Parses a date value returned by the API (assumed to be UTC) into a JS Date object
   * representing that same instant, so Angular's `date` pipe renders it in the browser's
   * local timezone. .NET serializes DateTime as an ISO string without a trailing 'Z' when
   * the Kind is Unspecified/Utc without offset info, so we normalize it before parsing.
   */
  private parseUtcDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;

    let dateStr = String(value);
    // If the string has no timezone designator (Z or +/-HH:mm), assume UTC and append 'Z'
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(dateStr)) {
      dateStr += 'Z';
    }

    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  loadInventoryTransactions(): void {
    this.loading = true;
    this.error = null;

    console.log('Loading recent inventory transactions from API...');

    this.apiService.get<any>('product/recentInventoryTransactions').subscribe({
      next: (data) => {
        console.log('API Response received:', data);
        console.log('Response type:', typeof data);
        console.log('Is Array?', Array.isArray(data));

        // Handle if data is wrapped in an object
        let transactionsArray: any[];
        if (Array.isArray(data)) {
          transactionsArray = data;
        } else if (data && data.transactions) {
          transactionsArray = data.transactions;
        } else if (data && data.data) {
          transactionsArray = data.data;
        } else if (data && data.value) {
          transactionsArray = data.value;
        } else {
          console.error('Unexpected response format:', data);
          transactionsArray = [];
        }

        console.log('Transactions array to display:', transactionsArray);
        console.log('Number of transactions:', transactionsArray.length);

        // Map the response to handle both camelCase and PascalCase
        const transactions = transactionsArray.map(t => ({
          id: t.id ?? t.Id,
          created: this.parseUtcDate(t.created ?? t.Created),
          createdBy: t.createdBy ?? t.CreatedBy,
          inventoryTransactionType: t.transactionType ?? t.TransactionType ?? '-',
          quantity: t.quantity ?? t.Quantity,
          productId: t.productId ?? t.ProductId ?? null,
          catalogNum: t.catalogNum ?? t.CatalogNum ?? '-',
          lotNumNumber: t.lotNumNumber ?? t.LotNumNumber ?? '-',
          salesOrderId: t.salesOrderId ?? t.SalesOrderId ?? null,
          salesOrderNumber: t.salesOrderNumber ?? t.SalesOrderNumber ?? '-',
          purchaseOrderId: t.purchaseOrderId ?? t.PurchaseOrderId ?? null,
          purchaseOrderNumber: t.purchaseOrderNumber ?? t.PurchaseOrderNumber ?? '-',
          invoiceId: t.invoiceId ?? t.InvoiceId ?? null,
          invoiceNumber: t.invoiceNumber ?? t.InvoiceNumber ?? '-',
        }));

        this.dataSource.data = transactions;
        this.loading = false;
        console.log('Transactions loaded into table:', transactions);
        console.log('Transactions count:', transactions.length);

        // Force change detection
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading customers:', err);
        console.error('Error status:', err.status);
        console.error('Error message:', err.message);

        if (err.status === 0) {
          this.error = 'Cannot connect to API. Make sure the Web API is running on https://localhost:44392 and SSL certificate is trusted.';
        } else if (err.status === 401) {
          this.error = 'Unauthorized. Please log out and log in again.';
        } else if (err.status === 404) {
          this.error = 'API endpoint not found. Check the API URL and endpoint.';
        } else {
          this.error = `Failed to load customers: ${err.message || 'Unknown error'}`;
        }

        this.loading = false;

        // Force change detection
        this.cdr.detectChanges();
      }
    });
  }

   applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

}
